import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import router from 'next/router';
import clsx from 'clsx';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Loading from '../atoms/Loading';
import Input from '../atoms/Input';
import Checkbox from '../atoms/Checkbox';
import TeaserBox from '../molecules/TeaserBox';
import ContentBox from '../molecules/ContentBox';
import StepNavi from '../molecules/StepNavi';
import PlacesSearch from '../organisms/PlacesSearch';
import { useTranslation } from 'next-i18next';
import { useAuth } from '../../context/AuthUserContext';
import { useAppSelector } from '../../app/hooks';
import {
  convertValue,
  clearObj,
  getErrorMsg,
  getFormData,
  byProperty,
} from '../../lib/utils';
import {
  mapGeoapifyToLocation,
  createLocation,
  updateLocation,
} from '../../lib/apis/location';
import { createCity } from '../../lib/apis/city';
import {
  createServiceCategory,
  deleteServiceCategory,
} from '../../lib/apis/serviceCategory';
import { isImperial, metricsName } from '../../features/metrics/metricsSlice';
import { convertValueToImperial, metricUnitKeys, Unit } from '../../lib/units';
import { createVenue, updateVenue } from '../../lib/apis/venue';
import {
  createServiceProvider,
  updateServiceProvider,
} from '../../lib/apis/serviceProvider';
import { createOptions } from '../../data/constants';
import { useApp } from '../../context/AppContext';
import {
  contact_details_fields,
  social_media_fields,
} from '../../data/display';
import { fields } from '../../data/fields';
import styles from '../../styles/components/organisms/CreateItem.module.scss';
import { cloudflareLoader } from '../atoms/Img';

interface CreateItemProps {
  className?: string;
  prefill?: any;
  apply?: (id: string, obj: any) => void;
}

export default function CreateItem(props: CreateItemProps) {
  const { t } = useTranslation(['common', 'pages', 'venue', 'service']);
  const { user } = useAuth();
  const { devMode } = useApp();
  const [type, setType] = useState('');
  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState([]) as any;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState() as any;
  const [data, setData] = useState({}) as any;
  const imperial = useAppSelector(isImperial);
  const unitsType = useAppSelector(metricsName);
  const { prefill, className } = props;
  const stepsClasses = clsx(
    styles.createSteps,
    { [styles.loading]: loading },
    className
  );
  const naviClasses = clsx(styles.createNavi, 'justify-content-center');
  const inputMax = 1200;
  const {
    id,
    name,
    headline,
    description,
    location,
    location_id,
    contact_details,
    memberships,
    categories,
  } = data;
  const { email } = contact_details || {};
  const { address } = location || {};
  const addressLine = address?.formatted;
  const formatted =
    prefill?.location?.address?.formatted || location?.address?.formatted;
  const contactDetailsFields = Object.keys(contact_details_fields);
  const socialMediaFields = Object.keys(social_media_fields);
  const contactFields = [...contactDetailsFields, ...socialMediaFields];
  const lastStep = steps.filter((str: string) => str !== 'intro').length;
  const currStep = steps[step];
  const currStepNr = steps.indexOf(currStep);
  const nextDisabled =
    !!error ||
    (currStep === 'type' && !type) ||
    (currStep === 'location' && (!name || name.length < 4 || !addressLine)) ||
    (currStep === 'service-location' &&
      (!name || name.length < 4 || !addressLine)) ||
    (currStep === 'description' && (!description || description.length < 10)) ||
    (currStep === 'categories' && (!categories || categories.length < 1)) ||
    (currStep === 'contact' && !email);
  const t_page = `pages:add-business:create:${currStep}`;
  const currContent = {
    s:
      currStep === 'intro'
        ? t(`${t_page}:step`)
        : `${t(`common:step`)} ${currStepNr} / ${lastStep}`,
    h: t(`${t_page}:headline`),
    p: t(`${t_page}:description`),
  };

  /**
   * Set property input fields
   */
  const businessProps = !!type
    ? Object.keys(createOptions[`${type}`]).map((key: string) => {
        const defaults = fields[key];
        const value = data[key] || (defaults ? defaults.value : '');
        const metricUnitKey = metricUnitKeys[unitsType][defaults.unit as Unit];
        const { maxLength, exp } = defaults;
        const placeholder =
          maxLength && !!exp
            ? t(`common:${exp}`, { maxLength })
            : typeof exp === 'number'
            ? `${t(`common:e.g.`)} ${exp}`
            : !!exp
            ? t(`common:${exp}`)
            : '';

        const fieldObj = {
          ...defaults,
          id: key,
          label:
            type === 'service'
              ? t(`${type}:categories:${key}`)
              : t(`${type}:${key}`),
          unit: t(`common:${metricUnitKey || defaults.unit}`),
          convertTo: !!metricUnitKey && imperial ? defaults.unit : '',
          placeholder,
          value:
            imperial && !!metricUnitKey
              ? convertValueToImperial(value, defaults.unit)
              : value,
        };
        return fieldObj;
      })
    : [];

  /**
   * Prefill fields with existing data if available
   */
  useEffect(() => {
    const localObj = localStorage.getItem('vs-create-item');
    const preData = localObj ? JSON.parse(localObj) : prefill || {};
    const { contact_details } = preData;
    const { email, website, phone } = contact_details || user || {};
    const type = preData.type || (devMode ? '' : 'venue');
    const prefillData = {
      ...preData,
      type,
      contact_details: {
        ...contact_details,
        email,
        website,
        phone,
      },
    };

    delete prefillData.images;
    delete prefillData.files;
    setData(prefillData);
    if (type) {
      setType(type);
      if (devMode) {
        localStorage.removeItem('vs-create-item');
        setStep(2);
      }
    }
  }, [prefill, user, setType, setData, devMode]);

  /**
   * Define form steps according to selected type of business
   */
  useEffect(() => {
    const commonSteps = ['intro', 'type'];
    const typeSteps = {
      venue: ['location', 'description', 'properties', 'contact'],
      service: ['service-location', 'description', 'categories', 'contact'],
    } as any;
    const allSteps = [...commonSteps, ...typeSteps[type || 'venue']];
    setSteps(allSteps);
  }, [type, devMode]);

  /**
   * Handle errors
   */
  const handleError = (error: any) => {
    console.error(error);
    const errorMsg = getErrorMsg(error);
    setError(errorMsg);
    setLoading(false);
  };

  /**
   * Handle mebmbership checkboxes input
   */
  const toggleMembership = (e: any) => {
    const key = Object.keys(e)[0];
    const tmp = (data.memberships || []).filter((el: string) => el !== key);
    const memberships = !!e[key] ? [...tmp, key] : tmp;
    setData({
      ...data,
      memberships,
    });
  };

  /**
   * Handle location input
   */
  // Set location data on googlePlaces select
  const setLocationData = (obj: any) => {
    const location = mapGeoapifyToLocation(obj) as any;
    const updObj = { ...data, location };
    setData(updObj);
  };

  // On proceed, check if location exists & create or update
  const saveLocation = () => {
    setError(false);
    setLoading(true);
    if (location_id) {
      updateItemLocation();
    } else {
      createItemLocation();
    }
  };

  // Create location then save business data
  const createItemLocation = () => {
    try {
      // Save new location to database and add current location_id to state.data
      createLocation(location).then((response) => {
        console.log('createLocation', location, response);
        const { country, city } = response.address;
        const updObj = {
          ...data,
          location: response,
          location_id: response.id,
          country,
          city,
        };
        saveItem(updObj);
      });
    } catch (error) {
      handleError(error);
    }
  };

  // Update Location then save business data
  const updateItemLocation = () => {
    const payload = clearObj({ ...location, id: null });
    try {
      // Save updated location to database and update current state.data
      updateLocation(location_id, payload).then((response: any) => {
        console.log('updateLocation', location, response);
        const { country, city } = response.address;
        const updObj = {
          ...data,
          location: response,
          country,
          city,
        };
        saveItem(updObj);
      });
    } catch (error) {
      handleError(error);
    }
  };

  /**
   * Handle business data input
   */
  // Check if business id exists & create or update
  const saveItem = (data: any) => {
    if (!data.location_id) {
      console.error('missing location_id');
      setError(t(`common:form-input-error`));
      return;
    }
    setError(false);
    setLoading(true);
    if (data.id) {
      updateItem(data);
    } else {
      createItem(data);
    }
  };

  // Create business on step navigation proceed
  const createItem = (data: any) => {
    // Get reference from query string
    const params = new URL(document.location.href).searchParams;
    const ref = params.get('ref') || undefined;
    // Set API create function dependend on type
    const create = type === 'venue' ? createVenue : createServiceProvider;
    // Reduce current data to expected payload fields
    const payload = clearObj({
      ...data,
      type: null,
      location: null,
      contact_details: null,
    });

    // Save new business item to database and extend current state.data with response data ( id )#
    try {
      create(payload, ref)
        .then((response) => {
          console.log('createItem', payload, response);
          const contactType = type === 'service' ? 'service_provider' : 'venue';
          const dataUpd = {
            ...data,
            ...response,
            contact_details: {
              ...data.contact_details,
              [`${contactType}_id`]: response.id,
            },
          };
          localStorage.setItem('vs-create-item', JSON.stringify(dataUpd));
          setData(dataUpd);
          setStep(step + 1);
        })
        .catch((error) => {
          handleError(error);
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (error) {
      handleError(error);
    }
  };

  // Update name or properties on step navigation proceed
  const updateItem = (data: any) => {
    // Set API update function dependend on type
    const update = type === 'venue' ? updateVenue : updateServiceProvider;
    // Reduce current data to expected payload fields
    const properties = type === 'venue' ? getFormData('itemProps') : {};
    const payload = clearObj({
      ...data,
      ...properties,
      type: null,
      id: null,
      slug: null,
      admins: null,
      status: null,
      location: null,
      location_id: null,
      categories: null,
      rates: null,
      rating: null,
    });

    // Save updated item to database
    update(id, payload)
      .then((response: any) => {
        console.log('updateItem', payload, response);
        const complete = currStepNr === lastStep;
        const dataUpd = {
          ...data,
          ...response,
          type,
        };
        localStorage.setItem('vs-create-item', JSON.stringify(dataUpd));
        if (complete && type === 'service') {
          saveCity(dataUpd);
        } else if (complete) {
          leave(dataUpd);
        } else {
          setLoading(false);
          setStep(step + 1);
        }
      })
      .catch((error) => {
        handleError(error);
      });
  };

  /**
   * Save Service Provider Categories
   */
  const toggleCategory = (e: any) => {
    const key = Object.keys(e)[0];
    const type = key.toUpperCase() as any;
    const curr = (data.categories || []).filter(
      (obj: any) => obj.type === type
    )[0];
    const categories = (data.categories || []).filter(
      (obj: any) => obj.type !== type
    );
    if (!!e[key] && !curr) {
      const payload = {
        service_provider_id: data.id,
        type,
      };
      try {
        createServiceCategory(payload)
          .then((response: any) => {
            console.log('createServiceCategory', response);
            setLoading(false);
            setData({
              ...data,
              categories: [...categories, response],
            });
          })
          .catch((error) => {
            handleError(error);
          });
      } catch (error) {
        handleError(error);
      }
    } else if (!!curr && !!curr.id) {
      try {
        deleteServiceCategory(curr.id)
          .then((response: any) => {
            console.log('deleteServiceCategory', curr.id, response);
            setLoading(false);
            setData({ ...data, categories });
          })
          .catch((error) => {
            handleError(error);
          });
      } catch (error) {
        handleError(error);
      }
    }
  };

  /**
   * Handle contact details input
   */
  const setContactDetails = (obj: any) => {
    setError('');
    const contact_details = {
      ...(data.contact_details || {}),
      ...obj,
    };
    const dataUpd = { ...data, contact_details };
    setData(dataUpd);
  };

  // Add service provider city to cities array
  const saveCity = (data: any) => {
    const { location } = data;
    const { address } = location;
    const { country, city } = address;
    const payload = {
      service_provider_id: data.id,
      location_id: data.location_id,
      country: country || data.country,
      city: city || data.city,
      approved_service_provider_id: null,
    };
    createCity(payload)
      .then((response: any) => {
        console.log('createCity', payload, response);
        leave(data);
      })
      .catch((error) => {
        handleError(error);
      });
  };

  // On complete proceed to edit page
  const leave = (data: any) => {
    localStorage.removeItem('vs-create-item');
    const { country, city, slug } = data;
    const nextPath =
      type === 'service'
        ? `/${type}-provider/${country}/${city}/${slug}/edit`
        : `/${type}/${country}/${city}/${slug}/edit`;
    router.push(nextPath);
  };

  /**
   * Handle step navigation
   */
  const proceed = () => {
    switch (currStep) {
      case 'location':
      case 'service-location':
        saveLocation();
        break;
      case 'description':
      case 'properties':
      case 'categories':
      case 'contact':
        saveItem(data);
        break;
      default:
        setError(false);
        setLoading(false);
        setStep(step + 1);
        break;
    }
  };

  return (
    <>
      {loading && <Loading overlay />}
      <Row className={stepsClasses}>
        {/**
         *  Intro
         * */}
        {currStep === 'intro' && (
          <Col sm={8} md={4}>
            <ContentBox img>
              <Image
                src={'/assets/common/keyvisual_tablet.jpg'}
                alt={'image'}
                layout="fill"
                objectFit="cover"
                objectPosition="left bottom"
                loader={cloudflareLoader}
              />
            </ContentBox>
          </Col>
        )}

        <Col sm={8} md={4}>
          {
            // Step Info
            <ContentBox gradient={currStep !== 'intro'} vCenter>
              <p>{currContent.s}</p>
              <h1>{currContent.h}</h1>
              <p>{currContent.p} </p>
            </ContentBox>
          }
        </Col>

        {currStep !== 'intro' && (
          <Col sm={8} md={4}>
            <ContentBox
              center={['type'].indexOf(currStep) > -1}
              scroll={
                ['properties', 'categories', 'contact'].indexOf(currStep) > -1
              }
            >
              {/**
               *  Business type
               * */}
              {currStep === 'type' && (
                <div>
                  {['venue', 'service'].map((key) => (
                    <TeaserBox
                      key={key}
                      className={styles.teaser}
                      active={type === key}
                      icon={`${key}s`}
                      title={t(`common:${key}`)}
                      onClick={() => {
                        setType(key);
                        setData({ ...data, type: key });
                        proceed();
                      }}
                    />
                  ))}
                </div>
              )}

              {/**
               *  Business name & address
               * */}
              {['location', 'service-location'].indexOf(currStep) > -1 && (
                <div>
                  <Input
                    name={'name'}
                    icon={type}
                    label={t(`${t_page}:input-name`)}
                    defaultValue={name}
                    onChange={(obj) => {
                      setError();
                      setData({
                        ...data,
                        name: convertValue(obj.name, 'text'),
                      });
                    }}
                  />
                  <Input
                    name={'headline'}
                    icon={'description'}
                    label={t(`${t_page}:input-headline`)}
                    defaultValue={headline}
                    onChange={(obj) => {
                      setError();
                      setData({
                        ...data,
                        headline: convertValue(obj.headline, 'text'),
                      });
                    }}
                  />
                  <PlacesSearch
                    label={t(`${t_page}:input-address`)}
                    defaultValue={formatted}
                    onChoose={setLocationData}
                  />

                  <div className={styles.checkboxes}>
                    <br />
                    {type === 'venue' && (
                      <>
                        <Checkbox
                          name="ufi"
                          defaultValue={(memberships || []).indexOf('ufi') > -1}
                          label={t(`common:ufi-member`)}
                          onChange={toggleMembership}
                        />
                        <Checkbox
                          defaultValue={
                            (memberships || []).indexOf('icca') > -1
                          }
                          name={'icca'}
                          label={t(`common:icca-member`)}
                          onChange={toggleMembership}
                        />
                      </>
                    )}
                    {type === 'service' && (
                      <>
                        <Checkbox
                          name="ifes"
                          defaultValue={
                            (memberships || []).indexOf('ifes') > -1
                          }
                          label={t(`common:ifes-member`)}
                          onChange={toggleMembership}
                        />
                      </>
                    )}
                  </div>
                  {/* <Input
                    name={'ufi'}
                    icon={'ufi'}
                    label={t(`{t_page}:input-ufi`)}
                    format='boolean'
                    defaultValue={
                      memberships && memberships.indexOf('ufi') > -1
                    }
                    onChange={(obj) => {
                      toggleMembership(obj);
                    }}
                  /> */}
                </div>
              )}

              {/**
               *  Headline & description
               * */}
              {currStep === 'description' && (
                <div className={styles.description}>
                  {/* <Input
                    name={'description'}
                    icon={"description"}
                    type="textarea"
                    label={t(`${t_page}:input-name`)}
                    defaultValue={description || ''}
                    placeholder={t(`common:input_lines`)}
                    onChange={(obj) => {
                      setData({
                        ...data,
                        name: convertValue(obj.description, 'text'),
                      });
                    }}
                  /> */}
                  <small>{`${
                    description ? description.length : 0
                  }/${inputMax}`}</small>
                  <textarea
                    placeholder={t(`common:input_lines`)}
                    maxLength={inputMax}
                    defaultValue={description || ''}
                    onChange={(e) =>
                      setData({
                        ...data,
                        description: e.currentTarget.value,
                      })
                    }
                  />
                </div>
              )}

              {/**
               *  Venue Properties
               * */}
              {currStep === 'properties' && (
                <form id="itemProps">
                  {businessProps.map((el, i) => (
                    <Input
                      add
                      reset
                      optional
                      key={i}
                      name={el.id}
                      icon={el.id}
                      label={el.label}
                      defaultValue={el.value}
                      type={el.type}
                      format={el.format}
                      unit={el.unit}
                      placeholder={el.placeholder}
                      maxLength={el.maxLength}
                      convertTo={el.convertTo}
                      onChange={(obj) => {
                        setData({
                          ...data,
                          [el.id]: convertValue(obj[el.id], el.format),
                        });
                      }}
                    />
                  ))}
                  <br />
                  <br />
                </form>
              )}
              {/**
               *  Service Categories
               * */}
              {currStep === 'categories' && (
                <form className={styles.categories}>
                  {businessProps
                    .filter((el) => el.id !== 'other')
                    .sort(byProperty('label'))
                    .map((el, i) => (
                      <Checkbox
                        key={i}
                        name={el.id}
                        label={el.label}
                        defaultValue={
                          !!categories &&
                          categories.length &&
                          categories.some((cat: any) => cat.type === el.id)
                        }
                        error={!!categories && !categories.length}
                        onChange={toggleCategory}
                        optional
                      />
                    ))}
                  {businessProps
                    .filter((el) => el.id === 'other')
                    .map((el, i) => (
                      <Checkbox
                        key={i}
                        name={el.id}
                        label={el.label}
                        defaultValue={
                          !!categories &&
                          categories.length &&
                          categories.some((cat: any) => cat.type === el.id)
                        }
                        error={!!categories && !categories.length}
                        onChange={toggleCategory}
                        optional
                      />
                    ))}
                  <br />
                  <br />
                </form>
              )}

              {/**
               *  Contact Details
               * */}
              {currStep === 'contact' && (
                <form id="contactDetails">
                  {contactFields.map((el, i) => (
                    <Input
                      key={i}
                      name={el}
                      icon={el}
                      format={el === 'phone' ? 'phone' : ''}
                      optional={el !== 'email'}
                      label={t(`common:${el}`)}
                      placeholder={
                        el === 'phone'
                          ? t(`common:input-phone-exp`, {
                              countryCode: '49',
                            })
                          : ''
                      }
                      unit={el === 'phone' ? t(`common:input-phone-help`) : ''}
                      defaultValue={contact_details[el]}
                      onChange={(obj) => setContactDetails(obj)}
                      onError={(error) => setError(error)}
                    />
                  ))}
                </form>
              )}

              {error && <div className={styles.error}>{error}</div>}
            </ContentBox>
          </Col>
        )}
      </Row>

      {/**
       * Step Navigation
       * */}
      <Row className={naviClasses}>
        <Col xs={7} md={3} xl={2}>
          <StepNavi
            steps={steps}
            currStep={step}
            disabled={loading}
            nextDisabled={nextDisabled}
            startLabel={t(`common:cta-create`)}
            endLabel={t(`common:overview`)}
            onNext={proceed}
            onBack={(num) => {
              setLoading(false);
              setStep(num);
            }}
            onComplete={proceed}
          />
        </Col>
      </Row>
    </>
  );
}
